import { CallStatuses, CallTypes, type Call } from '@entities/call';
import type { InactiveParticipant, Participant } from '@entities/participants';
import { ParticipantType } from '@entities/participants';
import { ControllerFactory } from '@main/factories/controller.factory';
import type { BaseUsecase } from '@usecases/base-usecase';
import type { UsecaseErrors } from '@usecases/usecase-errors';

export type SyncParticipantsRequest = {
  callId: string;
  activeParticipant?: {
    legId: string;
    isHeld: boolean;
    state: string;
    external?: {
      phoneNumber: string;
    };
    user?: {
      id: number;
      fullName: string;
    };
  };
  kind?: string;
  direction?: string;
  line?: {
    normalized: string;
  };
  me?: {
    legId: string;
    isHeld: boolean;
    state: string;
    user?: {
      id: number;
      fullName: string;
    };
    kind?: string;
    external?: {
      phoneNumber: string;
    };
  };
  inactiveParticipant?: {
    legId: string;
    isHeld: boolean;
    state: string;
    external?: {
      phoneNumber: string;
    };
    user?: {
      id: number;
      fullName: string;
    };
  };
};

export type SyncParticipantsErrors = Partial<
  UsecaseErrors<SyncParticipantsRequest>
>;

export class InvalidSyncParticipantsRequest extends Error {
  override name = 'InvalidSyncParticipantsRequest';
  errors: SyncParticipantsErrors;

  constructor(message: string, errors: SyncParticipantsErrors) {
    super(message);
    this.errors = errors;
  }
}

export interface SyncParticipantsValidator {
  validate(
    req: SyncParticipantsRequest,
  ): Promise<SyncParticipantsErrors | void>;
}

export interface SyncParticipantsRepository {
  addParticipants(callId: string, participants: Participant[]): Promise<void>;
  clearParticipants(): Promise<void>;
  clearExternalParticipant(): Promise<void>;
  getExternalParticipantByCallId(
    callId: string,
  ): Promise<InactiveParticipant | undefined>;
  addExternalParticipant(
    callId: string,
    participants: InactiveParticipant,
  ): Promise<void>;
}

export interface SyncParticipantsCallRepository {
  insertOutboundCall(call: Call): Promise<void>;
  getCalls(): Promise<Call[]>;
}

export interface SyncParticipantsUsecase
  extends BaseUsecase<SyncParticipantsRequest, void> {
  run(req: SyncParticipantsRequest): Promise<void>;
}

export class SyncParticipants implements SyncParticipantsUsecase {
  constructor(
    private validator: SyncParticipantsValidator,
    private repository: SyncParticipantsRepository,
    private callRepository: SyncParticipantsCallRepository,
  ) {}

  async run(req: SyncParticipantsRequest): Promise<void> {
    const validationErrors = await this.validator.validate(req);

    if (validationErrors) {
      throw new InvalidSyncParticipantsRequest(
        'Invalid sync participants request',
        validationErrors,
      );
    }

    const externalParticipant =
      await this.repository.getExternalParticipantByCallId(req.callId);

    if (req.kind === 'regular' || req.kind === 'internal') {
      // If current active participant is different from the external participant that means 1st party/customer has disconnected
      // and we need to hangup the call if it exists.,
      const externalParticipantLegId = externalParticipant?.legId;
      const activeParticipantLegId = req.activeParticipant?.legId;

      if (
        req.inactiveParticipant === undefined &&
        externalParticipantLegId &&
        activeParticipantLegId &&
        externalParticipantLegId !== activeParticipantLegId
      ) {
        const disconnectedCallController =
          ControllerFactory.buildDisconnectedCallController();
        const hangupTransferCallController =
          ControllerFactory.buildHangupTransferCallController();
        disconnectedCallController.handle();
        hangupTransferCallController.handle();
      }

      // Clear participants only for regular calls
      await this.repository.clearParticipants();
      await this.repository.clearExternalParticipant();
    }

    // Create participants array from activeParticipant and me
    const participants: Participant[] = [];

    if (req.activeParticipant) {
      participants.push({
        legId: req.activeParticipant.legId,
        isOnHold: req.activeParticipant.isHeld,
        state: req.activeParticipant.state,
        type:
          req.kind === 'warm-transfer'
            ? ParticipantType.THIRD_PARTY
            : req.activeParticipant.external?.phoneNumber
            ? ParticipantType.EXTERNAL
            : ParticipantType.AGENT,
        phoneNumber: req.activeParticipant.external?.phoneNumber,
      });

      //Added to get Aircall Uuid for Voicemail
      if (
        req.activeParticipant?.external?.phoneNumber &&
        req.direction === 'outgoing'
      ) {
        const existingCalls = await this.callRepository.getCalls();

        if (existingCalls && existingCalls.length == 1) {
          const outboundCall: Call = {
            id: req.callId,
            to: req.activeParticipant?.external?.phoneNumber ?? '',
            from: req.line?.normalized ?? '',
            type: CallTypes.OUTBOUND,
            status: CallStatuses.DIALING,
            isMuted: false,
            name: 'ForVoiceMailDrop',
          };
          await this.callRepository.insertOutboundCall(outboundCall);
        }
      }
    }

    if (req.me) {
      participants.push({
        legId: req.me.legId,
        isOnHold: req.me.isHeld,
        state: req.me.state,
        type: ParticipantType.AGENT,
        phoneNumber: req.me.external?.phoneNumber,
      });
    }

    if (!externalParticipant && req.inactiveParticipant) {
      await this.repository.addExternalParticipant(req.callId, {
        legId: req.inactiveParticipant.legId,
        isOnHold: req.inactiveParticipant.isHeld,
        state: req.inactiveParticipant.state,
        type: req.inactiveParticipant.external?.phoneNumber
          ? ParticipantType.EXTERNAL
          : ParticipantType.THIRD_PARTY,
        phoneNumber: req.inactiveParticipant.external?.phoneNumber,
      });
    }

    await this.repository.addParticipants(req.callId, participants);
  }
}
